import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import MobileNav from "./MobileNav";
import SqueakyCleanButton from "./SqueakyCleanButton";
import { useNavbarOverflow } from "../utils/navbarUtils";

const NAV_FLAG_ICON_URL = "https://forzabuilt.com/_vercel/image?url=%2Fimages%2Fmisc%2FFlag%20Icon%20with%20asterisk%201.webp&w=512&q=100";

export default function Nav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);
  
  // Get the current section type from the URL, excluding news section
  const currentSection = location.pathname.startsWith('/test') ? 'test' : 
                        location.pathname.startsWith('/household') ? 'household' :
                        location.pathname.startsWith('/industrial') ? 'industrial' :
                        'household'; // Default to household if no section is specified
  // Get the current page from the URL
  const currentPage = location.pathname.split('/')[2] || 'home';

  const links = [
    { name: "Home", path: currentSection === 'test' ? "/" : 
                       `/${currentSection}` },
    { name: "Products", path: currentSection === 'test' ? "/household/products" : 
                          `/${currentSection}/products` },
    { name: "News", path: `/${currentSection}/news` },
    { name: "About", path: currentSection === 'test' ? "/household/about" : 
                        `/${currentSection}/about` },
  ];

  // Check if navbar items are overflowing
  const isOverflowing = useNavbarOverflow('nav-container', 'nav-links');

  // Navbar height adjustment for large screens
  const navHeight = "h-20 md:h-24 lg:h-28";

  // Navbar background & text color logic
  const navClasses = `sticky top-0 z-50 bg-[#D3242A] text-white shadow-md ${navHeight}`;

  // Desktop link hover effect (rounded and snappy)
  const getDesktopLinkClass = (linkPath) => {
    const isActive = linkPath === location.pathname || 
                    (linkPath === `/${currentSection}` && location.pathname === `/${currentSection}/home`) ||
                    (linkPath === `/${currentSection}/products` && currentPage === 'product') ||
                    (linkPath === '/news' && location.pathname.startsWith('/news')) ||
                    (linkPath === '/test' && location.pathname === '/test');
    return `py-2 px-5 rounded-full transition-all duration-200 ease-in-out ${
      isActive 
        ? 'bg-white text-[#D3242A]' 
        : 'hover:bg-white hover:text-[#D3242A]'
    }`;
  };

  return (
    <motion.nav
      ref={navRef}
      id="nav-container"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        y: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }}
      className={navClasses}
    >
      <div className="container mx-auto flex justify-between items-center h-full px-6">
        {/* Logo */}
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ 
            delay: 0.3,
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.4 }
          }}
        >
          <Link to="/">
            <img 
              src="/images/RuggedRedTypographyLogo-InterlockDynamic.png" 
              alt="RuggedRed Logo" 
              className="h-16 lg:h-20" 
            />
          </Link>
        </motion.div>

        {/* Desktop Links */}
        {!isOverflowing ? (
          <ul className="hidden md:flex space-x-4 lg:space-x-8 text-base lg:text-lg xl:text-xl font-semibold nav-links" style={{ fontFamily: "Geogrotesque, sans-serif" }}>
            {links.map((link, index) => (
              <motion.li 
                key={link.name}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ 
                  delay: 0.3 + (index * 0.2),
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.4 }
                }}
                className={link.name === "Tips & News" ? "whitespace-nowrap" : ""}
              >
                <Link to={link.path} className={getDesktopLinkClass(link.path)}>
                  {link.name}
                </Link>
              </motion.li>
            ))}
          </ul>
        ) : (
          <div className="hidden md:block">
            <button onClick={() => setIsMenuOpen(true)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-current"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        )}

        {/* Flag Icon */}
        <motion.div 
          className="hidden md:flex items-center"
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ 
            delay: 0.9,
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.4 }
          }}
        >
          <img
            src={NAV_FLAG_ICON_URL}
            alt="Proudly Made in The USA"
            className="h-14 lg:h-16 w-auto ml-4 object-contain"
          />
        </motion.div>

        {/* Mobile Menu (Hamburger) */}
        <div className="md:hidden">
          <button onClick={() => setIsMenuOpen(true)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-current"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <MobileNav
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          links={links}
          currentPage={currentPage}
          currentSection={currentSection}
        />
      </div>
    </motion.nav>
  );
}